import type { SelfOpenRefusal, WidgetRenderer } from "./renderer";
import type { OperatorConfig, Suggestion } from "./types";
import { qualifiedOpenTargets, qualifiedSuppressionKey, suppressionAfterClose } from "./self-open-trigger";
import type { QualifiedOpen } from "./self-open-trigger";

/** The poll/close boundary owns suppression; renderer teardown cannot erase it. */
export class QualifiedSelfOpenController {
  private activeKey: string | null = null;
  private suppressed = new Set<string>();
  /** (match, reason) pairs already reported this tab session. */
  private reported = new Set<string>();
  /** ⭐ P0 baseline (2026-10-08): a qualified card wanted to push and a guard said
   *  no. Reported ONCE per match per reason per tab session — a poll every few
   *  seconds would otherwise send the same refusal hundreds of times. */
  onRefused: ((reason: SelfOpenRefusal | "suppressed", target: QualifiedOpen) => void) | null = null;
  /** A qualified push LANDED in the launcher pill (owner 2026-10-14: pushes are
   *  a pill, never a full opening). Before this, a delivered push sent nothing:
   *  the self-open count read ~0 from 10-15 while pushes kept firing. */
  onPushed: ((target: QualifiedOpen) => void) | null = null;

  constructor(
    private apiKey: string,
    private renderer: WidgetRenderer,
    private theme: OperatorConfig["theme"],
  ) {}

  poll(suggestions: Suggestion[]): boolean {
    // ⛔ THE FIRST QUALIFIED CARD THE DECK HOLDS, not the first one served. An
    // accepted acca now leads the served list, and the client drops combos the
    // book stopped taking — the renderer refused that target and the open
    // match's own single never got its turn.
    // ⛔ …and ONLY that one: a suppressed or refused choice is final, so the
    // card behind it cannot interrupt in its place.
    // ⛔ EXCEPT A MATCH THAT ALREADY PUSHED (Codex review 2026-10-08). Under the
    // per-match budget the open match stays the FIRST qualified card for as long
    // as the visitor is on it; refusing on it as `match_spent` blocked every
    // other match behind it for good. A spent match is skipped, not final — its
    // own cards (single behind an acca) are spent with it, so nothing on that
    // match can interrupt in its place.
    const targets = qualifiedOpenTargets(suggestions,
      this.theme.qualified_self_open === true, this.theme.self_open_signal === true)
      .filter((candidate) => this.renderer.holdsCard(candidate.cardId));
    const target = targets.find((candidate) => !this.renderer.isMatchPushed(candidate.eventId));
    if (!target) {
      if (targets[0]) this.refused("match_spent", targets[0]);
      return false;
    }
    const key = qualifiedSuppressionKey(this.apiKey, target.triggerId, target.cardId, target.eventId);
    let suppressed = this.suppressed.has(key);
    try { suppressed ||= sessionStorage.getItem(key) === "1"; } catch { /* memory fallback */ }
    if (suppressed) {
      this.refused("suppressed", target);
      return false;
    }
    if (!this.renderer.maybeSelfOpenPill(target.cardId, target.eventId)) {
      this.refused(this.renderer.lastSelfOpenRefusal ?? "show_failed", target);
      return false;
    }
    this.activeKey = key;
    this.suppressed.add(key);
    try { sessionStorage.setItem(key, "1"); } catch { /* memory fallback */ }
    this.onPushed?.(target);
    return true;
  }

  private refused(reason: SelfOpenRefusal | "suppressed", target: QualifiedOpen): void {
    if (!this.onRefused) return;
    const key = `bc_open_refused:${this.apiKey}:${target.eventId}:${reason}`;
    let seen = this.reported.has(key);
    try { seen ||= sessionStorage.getItem(key) === "1"; } catch { /* memory fallback */ }
    if (seen) return;
    this.reported.add(key);
    try { sessionStorage.setItem(key, "1"); } catch { /* memory fallback */ }
    this.onRefused(reason, target);
  }

  closed(reason: string): void {
    if (this.activeKey && suppressionAfterClose(reason) === "clear") {
      this.suppressed.delete(this.activeKey);
      try { sessionStorage.removeItem(this.activeKey); } catch { /* memory fallback */ }
    }
    this.activeKey = null;
  }

  /** A deliberate action is a reaction even when it does not close the peek.
   *
   * ⛔ NOT `"close"`. A close is now a DISMISSAL and KEEPS the suppression
   * (see self-open-trigger), so routing an add / star / match-open through it
   * would silently stop clearing the key the moment that rule changed — the
   * reaction path has to name a reason that means engagement. */
  reacted(): void {
    this.closed("added");
  }
}
